import {useCallback, useEffect, useRef, useState} from "react";

type Fn = () => void;

/**
 * @param seconds  start value; when isActive flips to true we RESET to this
 * @param isActive true → run/reset, false → pause (keep remaining time)
 * @param onExpire called when countdown reaches 0
 */
const useCountdown = (seconds: number, isActive: boolean, onExpire?: Fn) => {
    const [left, setLeft] = useState(seconds);
    const timerRef = useRef<number | null>(null);
    const deadlineRef = useRef<number>(0); // absolute time (ms) when we should hit 0
    const onExpireRef = useRef<Fn | undefined>(onExpire);

    useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    const clear = useCallback(() => {
        if (timerRef.current !== null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const tick = useCallback(() => {
        const ms = deadlineRef.current - Date.now();
        const next = Math.max(0, Math.ceil(ms / 1000)); // ceil avoids skipping the last second
        setLeft(next);
        if (ms <= 0) {
            clear();
            onExpireRef.current?.();
        }
    }, [clear]);

    const start = useCallback(() => {
        if (timerRef.current !== null) return;
        // run one immediate tick so UI updates without waiting a second
        tick();
        timerRef.current = window.setInterval(tick, 1000);
    }, [tick]);

    // Reset & start whenever isActive becomes true or seconds changes.
    useEffect(() => {
        clear();
        if (isActive) {
            setLeft(seconds);
            deadlineRef.current = Date.now() + seconds * 1000;
            start();
        }
        return clear;
    }, [isActive, seconds, start, clear]);

    // Pause timer when tab hidden; resume without resetting when visible again.
    useEffect(() => {
        if (!isActive) return;
        const onVis = () => {
            if (document.visibilityState === "hidden") {
                clear(); // stop interval to save CPU
            } else {
                // don't change deadline; just resume and immediately recompute 'left'
                start();
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [isActive, start, clear]);

    const mm = String(Math.floor(left / 60)).padStart(2, "0");
    const ss = String(left % 60).padStart(2, "0");

    return {left, label: `${mm}:${ss}`};
};

export default useCountdown;
